import {inject, Injectable, signal} from '@angular/core';
import {MsalService} from '@azure/msal-angular';
import {jwtDecode} from "jwt-decode";
import {UserContext} from "../../core/models";
import {BrowserAuthError, InteractionRequiredAuthError} from "@azure/msal-browser";

export interface MicrosoftProfile {
    mail: string | null;
    jobTitle: string | null;
    department: string | null;
}

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly msalService = inject(MsalService);

    // state
    private readonly userPrincipal = signal<UserContext | null>(null);
    private readonly userSettings = signal<Record<string, unknown>>({});

    public readonly userContext = this.userPrincipal.asReadonly();

    private getDecodedToken(): { groups: string[]; name: string; exp?: number; iat?: number } | null {
        const account =
            this.msalService.instance.getActiveAccount() ??
            this.msalService.instance.getAllAccounts()[0];

        const idToken = account?.idToken;
        if (!idToken) return null;

        try {
            return jwtDecode<{ groups: string[]; name: string }>(idToken);
        } catch {
            return null;
        }
    }

    public resetUserPrincipal(): void {
        this.userPrincipal.set(null);
        this.userSettings.set({});

        const msalInstance = this.msalService.instance;
        const account =
            msalInstance.getActiveAccount() ??
            msalInstance.getAllAccounts()[0] ??
            null;

        this.msalService
            .logoutRedirect({
                account: account ?? undefined,
                postLogoutRedirectUri: '/',
            })
    }

    public getGroups() {
        return this.getDecodedToken()?.groups ?? [];
    }

    public getName() {
        return this.getDecodedToken()?.name ?? '';
    }

    /**
     * Fetches the Microsoft Graph profile for the active account
     */
    public async getMicrosoftProfile(): Promise<MicrosoftProfile | null> {
        const msalInstance = this.msalService.instance;
        const account = msalInstance.getActiveAccount();
        if (!account) throw new Error('No active account');
        try {
            const decoded = this.getDecodedToken();
            const now = Date.now();
            if (!decoded?.exp || !decoded?.iat || decoded.iat * 1000 > now || decoded.exp * 1000 < now) {
                throw new InteractionRequiredAuthError('token_expired');
            }
            const response = await msalInstance.acquireTokenSilent({scopes: ['User.Read'], account});
            const profile = await fetch("https://graph.microsoft.com/v1.0/me?$select=mail,jobTitle,department", {
                headers: {Authorization: `Bearer ${response.accessToken}`}
            }).then(r => r.json());

            this.userPrincipal.set({
                name: decoded?.name ?? '',
                mail: profile.mail ?? null,
                jobTitle: profile.jobTitle ?? null,
                department: profile.department ?? null,
                groups: decoded?.groups ?? []
            });
            return profile;
        } catch (e) {
            if (
                e instanceof InteractionRequiredAuthError ||
                (e instanceof BrowserAuthError && e.errorCode === 'timed_out')
            ) {
                await msalInstance.acquireTokenRedirect({scopes: ['User.Read'], account});
            } else {
                throw e;
            }
            return null;
        }
    }
}
